import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../shared/lib/supabase'

const STATUS_CACHE_KEY = 'ssa-app-status-cache'
const CACHE_TTL = 60 * 1000 // 1 minute

/**
 * Infrastructure services checked via public status APIs.
 */
const INFRASTRUCTURE = [
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    description: 'CDN, DNS, and Pages hosting',
    statusPage: 'https://www.cloudflarestatus.com',
    statusUrl: 'https://www.cloudflarestatus.com/api/v2/status.json',
    parseStatus: (data) => {
      const indicator = data?.status?.indicator
      if (indicator === 'none') return { status: 'operational', detail: 'All systems operational' }
      if (indicator === 'minor') return { status: 'degraded', detail: data?.status?.description || 'Minor issues' }
      if (indicator === 'major') return { status: 'outage', detail: data?.status?.description || 'Major outage' }
      if (indicator === 'critical') return { status: 'outage', detail: data?.status?.description || 'Critical outage' }
      return { status: 'unknown', detail: 'Unable to determine status' }
    }
  },
  {
    id: 'supabase',
    name: 'Supabase',
    description: 'Database, Auth, and Storage',
    statusPage: 'https://status.supabase.com',
    check: async () => {
      try {
        const start = Date.now()
        const { error } = await supabase.from('profiles').select('id', { count: 'exact', head: true })
        const latency = Date.now() - start
        if (error) return { status: 'degraded', detail: `Responded with error: ${error.message}`, latency }
        return { status: 'operational', detail: `Healthy (${latency}ms)`, latency }
      } catch {
        return { status: 'outage', detail: 'Connection failed' }
      }
    }
  }
]

/**
 * API endpoints to health-check.
 */
const API_ENDPOINTS = [
  { id: 'api-global-metrics', name: 'Global Metrics', path: '/api/global/metrics', method: 'GET', authRequired: true },
  { id: 'api-global-activity', name: 'Global Activity', path: '/api/global/activity', method: 'GET', authRequired: true },
  { id: 'api-ai-classify', name: 'AI Classification', path: '/api/ai/cdm-classify', method: 'POST', authRequired: false, skipCheck: true, description: 'Anthropic Claude Haiku' },
  { id: 'api-ai-policy', name: 'AI Policy Generation', path: '/api/ai/policy-generate', method: 'POST', authRequired: false, skipCheck: true, description: 'Anthropic Claude' },
  { id: 'api-users', name: 'User Management', path: '/api/users', method: 'POST', authRequired: true, skipCheck: true },
  { id: 'api-activity-log', name: 'Activity Logging', path: '/api/activity-log', method: 'POST', authRequired: true, skipCheck: true },
  { id: 'api-organizations', name: 'Organizations', path: '/api/organizations', method: 'GET', authRequired: true }
]

/**
 * App pages to verify render.
 */
const APP_PAGES = [
  { id: 'page-dashboard', name: 'Dashboard', path: '/', section: 'Admin' },
  { id: 'page-clients', name: 'Clients', path: '/clients', section: 'Admin' },
  { id: 'page-users', name: 'User Management', path: '/users', section: 'Admin' },
  { id: 'page-policies', name: 'Policy Library', path: '/policies', section: 'Admin' },
  { id: 'page-regulatory', name: 'Regulatory Updates', path: '/regulatory-updates', section: 'Admin' },
  { id: 'page-audit', name: 'Audit Logs', path: '/audit-logs', section: 'Admin' },
  { id: 'page-settings', name: 'Settings', path: '/settings', section: 'Admin' },
  { id: 'page-portal', name: 'Client Portal Home', path: '/portal', section: 'Portal' },
  { id: 'page-portal-results', name: 'Portal Results', path: '/portal/results', section: 'Portal' },
  { id: 'page-portal-policies', name: 'Portal Policies', path: '/portal/policies', section: 'Portal' },
  { id: 'page-portal-vault', name: 'Portal Vault', path: '/portal/vault', section: 'Portal' },
  { id: 'page-portal-intake', name: 'Portal Intake', path: '/portal/intake', section: 'Portal' },
  { id: 'page-global-dashboard', name: 'Global Dashboard', path: '/global', section: 'Global Admin' },
  { id: 'page-global-orgs', name: 'Organizations', path: '/global/organizations', section: 'Global Admin' },
  { id: 'page-global-activity', name: 'Activity Log', path: '/global/activity', section: 'Global Admin' },
  { id: 'page-global-security', name: 'Security Events', path: '/global/security', section: 'Global Admin' },
  { id: 'page-global-notifications', name: 'Notifications', path: '/global/notifications', section: 'Global Admin' },
  { id: 'page-global-settings', name: 'Platform Settings', path: '/global/settings', section: 'Global Admin' },
  { id: 'page-global-status', name: 'Platform Status', path: '/global/status', section: 'Global Admin' }
]

async function checkInfrastructure(service) {
  if (service.check) {
    return service.check()
  }
  if (service.statusUrl) {
    try {
      const res = await fetch(service.statusUrl)
      if (!res.ok) return { status: 'unknown', detail: `Status page returned ${res.status}` }
      const data = await res.json()
      return service.parseStatus(data)
    } catch {
      return { status: 'unknown', detail: 'Could not reach status page' }
    }
  }
  return { status: 'unknown', detail: 'No check configured' }
}

async function checkApiEndpoint(endpoint, token) {
  if (endpoint.skipCheck) {
    return { status: 'skipped', detail: 'Write-only endpoint — not health-checked' }
  }
  try {
    const headers = {}
    if (endpoint.authRequired && token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    const start = Date.now()
    const res = await fetch(endpoint.path, {
      method: endpoint.method,
      headers
    })
    const latency = Date.now() - start

    if (res.ok) return { status: 'operational', detail: `${res.status} (${latency}ms)`, latency }
    if (res.status === 401 || res.status === 403) return { status: 'operational', detail: `Auth required — endpoint reachable (${latency}ms)`, latency }
    if (res.status >= 500) return { status: 'outage', detail: `Server error ${res.status}`, latency }
    return { status: 'degraded', detail: `Returned ${res.status} (${latency}ms)`, latency }
  } catch {
    return { status: 'outage', detail: 'Connection failed' }
  }
}

export default function useAppStatus() {
  const [results, setResults] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(STATUS_CACHE_KEY))
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) return cached.data
    } catch { /* ignore */ }
    return null
  })
  const [loading, setLoading] = useState(false)
  const [lastChecked, setLastChecked] = useState(null)

  const runChecks = useCallback(async () => {
    setLoading(true)

    // Get auth token for API checks
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token

    // Run infrastructure checks in parallel
    const infraResults = await Promise.all(
      INFRASTRUCTURE.map(async (svc) => ({
        ...svc,
        result: await checkInfrastructure(svc)
      }))
    )

    // Run API checks in parallel
    const apiResults = await Promise.all(
      API_ENDPOINTS.map(async (ep) => ({
        ...ep,
        result: await checkApiEndpoint(ep, token)
      }))
    )

    const now = new Date().toISOString()
    const data = {
      infrastructure: infraResults,
      apis: apiResults,
      pages: APP_PAGES,
      timestamp: now
    }

    setResults(data)
    setLastChecked(now)
    setLoading(false)

    // Cache results
    localStorage.setItem(STATUS_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }))
  }, [])

  useEffect(() => {
    if (!results) runChecks()
  }, [results, runChecks])

  // Compute summary counts
  const summary = {
    operational: 0,
    degraded: 0,
    outage: 0,
    unknown: 0,
    skipped: 0
  }
  if (results) {
    for (const svc of results.infrastructure) {
      summary[svc.result.status] = (summary[svc.result.status] || 0) + 1
    }
    for (const ep of results.apis) {
      summary[ep.result.status] = (summary[ep.result.status] || 0) + 1
    }
  }

  return { results, loading, lastChecked, runChecks, summary }
}
